import { useQuery } from '@tanstack/react-query';

import { addonList } from '@api/sdk.gen';
import {
  AddonHealthStatus,
  AddonLifecycleStatus,
  AddonsAddonListResponse,
} from '@api/types.gen';

import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../query-keys';

const REFETCH_INTERVAL_WHILE_IN_PROGRESS = 10_000;
// A catalog error is usually transient, so the view recovers on its own. Slower
// than the lifecycle poll: nothing is mid-operation.
const REFETCH_INTERVAL_WHILE_CATALOG_FAILING = 30_000;

// Slower than an operation in flight: a credential fault is a steady state that
// may never clear, and every poll costs Portainer a live probe of the add-on.
const REFETCH_INTERVAL_WHILE_CREDENTIAL_INVALID = 15_000;

export function useAddons<T = AddonsAddonListResponse | undefined>({
  enabled = true,
  select,
}: {
  enabled?: boolean;
  select?: (data: AddonsAddonListResponse | undefined) => T;
} = {}) {
  return useQuery({
    queryKey: queryKeys.list(),
    queryFn: getAddons,
    enabled,
    select,
    // From the raw response, so a narrowing `select` can't hide in-progress
    // states or a catalog error.
    refetchInterval: (_data, query) => pollInterval(query.state.data),
    ...withError('Unable to load add-ons'),
  });
}

/**
 * useCatalogError reports why the add-on catalog could not be refreshed, or
 * undefined when it loaded. Distinguishing that from an add-on the catalog
 * simply does not list is what keeps the two explanations apart; see
 * catalog-messages.
 */
export function useCatalogError() {
  return useAddons({ select: (data) => data?.catalogError }).data;
}

export function pollInterval(data?: AddonsAddonListResponse) {
  if (hasAddonInProgress(data)) {
    return REFETCH_INTERVAL_WHILE_IN_PROGRESS;
  }

  if (hasCredentialInvalid(data)) {
    return REFETCH_INTERVAL_WHILE_CREDENTIAL_INVALID;
  }

  if (data?.catalogError) {
    return REFETCH_INTERVAL_WHILE_CATALOG_FAILING;
  }

  return false as const;
}

// A repair leaves the lifecycle untouched, so the badge would otherwise sit on
// credential-invalid until something else refetched the list. See pollInterval
// in useAddon.
function hasCredentialInvalid(data?: AddonsAddonListResponse) {
  return Boolean(
    data?.addons?.some(
      (addon) =>
        addon.healthStatus === AddonHealthStatus.ADDON_HEALTH_CREDENTIAL_INVALID
    )
  );
}

function hasAddonInProgress(data?: AddonsAddonListResponse) {
  return Boolean(
    data?.addons?.some(
      (addon) =>
        addon.lifecycleStatus ===
          AddonLifecycleStatus.ADDON_STATUS_INSTALLING ||
        addon.lifecycleStatus ===
          AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING ||
        addon.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UPGRADING
    )
  );
}

async function getAddons() {
  const { data } = await addonList({});
  return data;
}
